import { useRef } from 'react';
import { SWRConfiguration, SWRResponse } from 'swr';
import useSWRImmutable from 'swr/immutable';

type Mutator<T> = (current: T | undefined) => T | undefined;

type SWRWithRollback<T> = SWRResponse<T> & {
    optimisticUpdate: (updater: Mutator<T>) => void;
    rollback: () => void;
};

const useSWRImmutableWithRollback = <T = unknown>(
    key: string | undefined,
    fetcher: () => Promise<T>,
    options?: SWRConfiguration
): SWRWithRollback<T> => {
    const swr = useSWRImmutable<T>(key, fetcher, options);
    const prev_data_ref = useRef<T | undefined>(undefined);

    const optimisticUpdate = (updater: Mutator<T>): void => {
        prev_data_ref.current = swr.data;
        const updated = updater(swr.data);
        if (updated !== undefined) {
            void swr.mutate(updated, { revalidate: false });
        }
    };

    const rollback = (): void => {
        if (prev_data_ref.current !== undefined) {
            void swr.mutate(prev_data_ref.current, { revalidate: false });
        }
    };

    return {
        ...swr,
        optimisticUpdate,
        rollback,
    };
};

export default useSWRImmutableWithRollback;
